import React, { useContext, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useEventListener, useUnmountEffect } from 'primereact/hooks';
import { classNames } from 'primereact/utils';
import AppFooter from './AppFooter';
import AppSidebar from './AppSidebar';
import AppTopbar from './AppTopbar';
import AppConfig from './AppConfig';
import { LayoutContext } from './context/LayoutContext';
import PrimeReact from 'primereact/api';
import { Helmet } from 'react-helmet';
import { ChildContainerProps, LayoutState, AppTopbarRef } from '../types/types';

interface LayoutProps extends ChildContainerProps {
    hideElements?: boolean; // Add a prop to toggle visibility of elements
}

const Layout: React.FC<LayoutProps> = ({ children, hideElements }) => {
    const { layoutConfig, layoutState, setLayoutState } = useContext(LayoutContext);
    const topbarRef = useRef<AppTopbarRef>(null);
    const sidebarRef = useRef<HTMLDivElement>(null);
    const location = useLocation();

    useEffect(() => {
        PrimeReact.ripple = true;
    }, []);

    useEffect(() => {
        hideMenu(); 
    }, [location.pathname]);


    const containerClass = classNames('layout-wrapper', {
        'layout-overlay': layoutConfig.menuMode === 'overlay',
        'layout-static': layoutConfig.menuMode === 'static',
        'layout-static-inactive': layoutState.staticMenuDesktopInactive && layoutConfig.menuMode === 'static',
        'layout-overlay-active': layoutState.overlayMenuActive,
        'layout-mobile-active': layoutState.staticMenuMobileActive,
        'p-input-filled': layoutConfig.inputStyle === 'filled',
        'p-ripple-disabled': !layoutConfig.ripple,
    });

    const hideMenu = () => {
        setLayoutState((prevLayoutState: LayoutState) => ({ ...prevLayoutState, overlayMenuActive: false, staticMenuMobileActive: false, menuHoverActive: false }));
    };
    const hideProfileMenu = () => {
        setLayoutState((prevLayoutState: LayoutState) => ({ ...prevLayoutState, profileSidebarVisible: false }));
        unbindProfileMenuOutsideClickListener();
    };

    const [bindMenuOutsideClickListener, unbindMenuOutsideClickListener] = useEventListener({
        type: 'click',
        listener: (event) => {
            const isOutsideClicked = (
                sidebarRef.current?.contains(event.target as Node) ||
                topbarRef.current?.topbarmenubutton?.isSameNode(event.target as Node) ||
                topbarRef.current?.topbarmenubutton?.contains(event.target as Node) ||
                topbarRef.current?.menubutton?.contains(event.target as Node)
            );
    
            if (!isOutsideClicked) {
                hideMenu();
            }
        }
    });

    const [bindProfileMenuOutsideClickListener, unbindProfileMenuOutsideClickListener] = useEventListener({
        type: 'click',
        listener: (event) => {
            const isOutsideClicked = (
                topbarRef.current?.topbarmenu?.isSameNode(event.target as Node) ||
                topbarRef.current?.topbarmenu?.contains(event.target as Node) ||
                topbarRef.current?.topbarmenubutton?.isSameNode(event.target as Node) ||
                topbarRef.current?.topbarmenubutton?.contains(event.target as Node)
            );
    
            if (!isOutsideClicked) {
                hideProfileMenu();
            }
        }
    });

    useEffect(() => {
        if (layoutState.overlayMenuActive || layoutState.staticMenuMobileActive) {
            bindMenuOutsideClickListener();
        } else {
            unbindMenuOutsideClickListener();
        }
    
        return () => {
            unbindMenuOutsideClickListener();
        };
    }, [layoutState.overlayMenuActive, layoutState.staticMenuMobileActive]);

    useEffect(() => {
        if (layoutState.profileSidebarVisible) {
            bindProfileMenuOutsideClickListener();
        }
    }, [layoutState.profileSidebarVisible]);

    return (
        <React.Fragment>
            <Helmet>
                <title>{process.env.REACT_APP_SYSTEM_NAME}</title>
                <meta charSet="UTF-8" />
                <meta name="description" content="The ultimate collection of design-agnostic, flexible and accessible React UI Components." />
                <meta name="viewport" content="initial-scale=1, width=device-width" />
            </Helmet>

            <div className={containerClass}>
                {/* Conditionally render navbar, sidebar, and footer */}
                {!hideElements && <AppTopbar ref={topbarRef} />}
                {!hideElements && (
                    <div ref={sidebarRef} className="layout-sidebar">
                        <AppSidebar />
                    </div>
                )}
                <div className="layout-main-container">
                    <div className="layout-main">
                        {children}
                    </div>
                    {!hideElements && <AppFooter />}
                </div>
                {!hideElements && <AppConfig />}
                <div className="layout-mask"></div>
            </div>
        </React.Fragment>
    );
};

export default Layout;
